// utils/yandexMetrika.js

// ID счётчиков (можно несколько)
const COUNTER_IDS = [99943278, 99942553];
// Ключ согласия в localStorage
const CONSENT_KEY = 'cookieConsent';

// UIS (коллтрекинг) — тоже отложен до согласия
const UIS_SRC = 'https://app.uiscom.ru/static/cs.min.js?k=XeN3Yg6fVPo6gUrZcHvo3QylgmIpziTT';

// Получено ли согласие на трекинг
export const hasTrackingConsent = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(CONSENT_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

// Сохранить согласие
export const setTrackingConsent = (granted) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_KEY, granted ? 'true' : 'false');
  } catch (e) {
    // ignore
  }
};

// Инициализация Метрики и UIS — вызывается ТОЛЬКО после получения согласия
let initialized = false;
export const initTracking = () => {
  if (initialized) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  initialized = true;

  // 1) Подгружаем tag.js Яндекс.Метрики
  const tagAlreadyLoaded = Array.from(document.scripts).some(
    (s) => s.src === 'https://mc.yandex.ru/metrika/tag.js'
  );
  if (!tagAlreadyLoaded) {
    const k = document.createElement('script');
    const a = document.getElementsByTagName('script')[0];
    k.async = 1;
    k.src = 'https://mc.yandex.ru/metrika/tag.js';
    a.parentNode.insertBefore(k, a);
  }

  // 2) Инициализируем все счётчики
  COUNTER_IDS.forEach((id) => {
    try {
      window.ym(id, 'init', {
        webvisor: true,
        clickmap: true,
        referrer: document.referrer,
        url: window.location.href,
        accurateTrackBounce: true,
        trackLinks: true,
      });
    } catch (e) {
      console.error('Не удалось инициализировать Яндекс.Метрику', id, e);
    }
  });

  // 3) Подгружаем UIS (коллтрекинг)
  const uisAlreadyLoaded = Array.from(document.scripts).some((s) => s.src === UIS_SRC);
  if (!uisAlreadyLoaded) {
    const u = document.createElement('script');
    u.async = true;
    u.src = UIS_SRC;
    document.head.appendChild(u);
  }
};

// Проверка доступности метрики (стаб ym есть всегда после index.html,
// но реальные вызовы должны идти только после initTracking)
const isMetrikaAvailable = () => {
  return typeof window !== 'undefined' && typeof window.ym === 'function' && initialized;
};

// Безопасная обертка для вызовов метрики
const safeMetrikaCall = (callback) => {
  if (isMetrikaAvailable()) {
    try {
      callback();
    } catch (error) {
      console.error('Ошибка Яндекс.Метрики:', error);
    }
  }
};

// Отслеживание целей
export const trackGoal = (goalName, params = {}) => {
  safeMetrikaCall(() => {
    COUNTER_IDS.forEach((counterId) => window.ym(counterId, 'reachGoal', goalName, params));
    console.log(`Цель отправлена: ${goalName}`, params);
  });
};

// Отслеживание событий
export const trackEvent = (category, action, label = null, value = null) => {
  safeMetrikaCall(() => {
    const eventParams = {
      event_category: category,
      event_action: action,
    };
    
    if (label) eventParams.event_label = label;
    if (value) eventParams.value = value;
    
    COUNTER_IDS.forEach((counterId) => window.ym(counterId, 'params', eventParams));
    console.log('Событие отправлено:', eventParams);
  });
};

// Отслеживание переходов между страницами
export const trackPageView = (url, options = {}) => {
  safeMetrikaCall(() => {
    COUNTER_IDS.forEach((counterId) => window.ym(counterId, 'hit', url, options));
  });
};

// Отслеживание внешних ссылок
export const trackOutboundLink = (url, callback) => {
  safeMetrikaCall(() => {
    COUNTER_IDS.forEach((counterId) => window.ym(counterId, 'extLink', url));
  });
  
  // Выполняем callback через небольшую задержку
  if (callback) {
    setTimeout(callback, 200);
  }
};

// Отслеживание скачивания файлов
export const trackFileDownload = (fileUrl) => {
  safeMetrikaCall(() => {
    COUNTER_IDS.forEach((counterId) => window.ym(counterId, 'file', fileUrl));
  });
};

// Отслеживание электронной коммерции (для будущего)
export const trackEcommerce = (action, data) => {
  safeMetrikaCall(() => {
    COUNTER_IDS.forEach((counterId) => window.ym(counterId, 'ecommerce', action, data));
  });
};

// Список целей для удобства
export const GOALS = {
  // Формы
  FORM_SUBMIT: 'form_submit',
  FORM_OPEN: 'form_open',
  FORM_FILL_START: 'form_fill_start',
  
  // Услуги
  SERVICE_VIEW: 'service_view',
  SERVICE_CLICK: 'service_click',
  
  // Контакты
  PHONE_CLICK: 'phone_click',
  WHATSAPP_CLICK: 'whatsapp_click',
  EMAIL_CLICK: 'email_click',
  ADDRESS_CLICK: 'address_click',
  
  // Навигация
  MENU_CLICK: 'menu_click',
  SCROLL_TO_FORM: 'scroll_to_form',
  
  // Вовлеченность
  PAGE_SCROLL_25: 'page_scroll_25',
  PAGE_SCROLL_50: 'page_scroll_50',
  PAGE_SCROLL_75: 'page_scroll_75',
  PAGE_SCROLL_100: 'page_scroll_100',
  TIME_ON_PAGE_30: 'time_on_page_30',
  TIME_ON_PAGE_60: 'time_on_page_60',
};

// Инициализация отслеживания глубины прокрутки
export const initScrollTracking = () => {
  let scrollPercentages = [25, 50, 75, 100];
  let reachedPercentages = new Set();
  
  const checkScroll = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollPercent = Math.round((scrollTop + windowHeight) / documentHeight * 100);
    
    scrollPercentages.forEach(percentage => {
      if (scrollPercent >= percentage && !reachedPercentages.has(percentage)) {
        reachedPercentages.add(percentage);
        trackGoal(`PAGE_SCROLL_${percentage}`, { scroll_depth: percentage });
      }
    });
  };
  
  // Debounce для оптимизации
  let scrollTimer;
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(checkScroll, 100);
  });
};

// Инициализация отслеживания времени на странице
export const initTimeTracking = () => {
  const timeGoals = [
    { seconds: 30, goal: GOALS.TIME_ON_PAGE_30 },
    { seconds: 60, goal: GOALS.TIME_ON_PAGE_60 }
  ];
  
  timeGoals.forEach(({ seconds, goal }) => {
    setTimeout(() => {
      trackGoal(goal, { time_on_page: seconds });
    }, seconds * 1000);
  });
};
